import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { createContext, useContext, useRef } from 'react';

interface ActionMenuState {
  open: boolean;
  focusTransfer: React.MutableRefObject<boolean>;
}

const ActionMenuContext = createContext<ActionMenuState | null>(null);

interface ActionMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  className?: string;
  side?: 'top' | 'bottom';
  align?: 'start' | 'end';
  /** Elements matching this selector toggle the menu themselves, so they must not dismiss it. */
  toggleSelector?: string;
  /** Class for the wrapper element, e.g. to take it out of the layout of its parent. */
  rootClassName?: string;
  /** Where the menu is rendered; defaults to `body`. A host that hides on focus loss needs its own element. */
  portalContainer?: HTMLElement | null;
  onOpenAutoFocus?: (event: Event) => void;
  /** Runs first; calling `preventDefault` skips the default focus return to the trigger. */
  onCloseAutoFocus?: (event: Event) => void;
  trigger: React.ReactElement;
  children: React.ReactNode;
}

const MENU_OFFSET = 4;
const VIEWPORT_MARGIN = 8;

export function ActionMenu({
  open,
  onOpenChange,
  label,
  className,
  side = 'bottom',
  align = 'end',
  toggleSelector,
  rootClassName,
  portalContainer,
  onOpenAutoFocus,
  onCloseAutoFocus,
  trigger,
  children,
}: ActionMenuProps): React.ReactElement {
  const focusTransfer = useRef(false);

  return (
    <div
      data-menu-root
      className={rootClassName}
      draggable={false}
      // Portalled content still bubbles React events to the host, e.g. a clickable card
      // that opens on Enter. Radix listens for Escape on the document, so it is unaffected.
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      onDragStart={(event) => {
        // Menu content is portalled but still bubbles to draggable hosts such as page rows.
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <DropdownMenu.Root
        open={open}
        modal={false}
        onOpenChange={(next) => {
          if (next) focusTransfer.current = false;
          onOpenChange(next);
        }}
      >
        <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
        <DropdownMenu.Portal {...(portalContainer ? { container: portalContainer } : {})}>
          <DropdownMenu.Content
            data-menu-root
            className={className ? `menu menu--action ${className}` : 'menu menu--action'}
            aria-label={label}
            side={side}
            align={align}
            sideOffset={MENU_OFFSET}
            collisionPadding={VIEWPORT_MARGIN}
            onInteractOutside={(event) => {
              const target = event.target;
              if (toggleSelector && target instanceof Element && target.closest(toggleSelector)) {
                event.preventDefault();
              }
            }}
            {...(onOpenAutoFocus ? { onOpenAutoFocus } : {})}
            onCloseAutoFocus={(event) => {
              onCloseAutoFocus?.(event);
              // Actions that focus their own input or dialog keep that focus.
              if (focusTransfer.current) event.preventDefault();
            }}
          >
            <ActionMenuContext.Provider value={{ open, focusTransfer }}>
              {children}
            </ActionMenuContext.Provider>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

function useActionMenuState(): ActionMenuState {
  const state = useContext(ActionMenuContext);
  if (!state) throw new Error('Action menu items must be rendered inside ActionMenu');
  return state;
}

function itemClass(danger: boolean): string {
  return danger ? 'menu__item menu__item--danger nb' : 'menu__item nb';
}

interface ActionMenuItemProps extends Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'onSelect' | 'type'
> {
  onSelect: () => void;
  transfersFocus?: boolean;
  keepOpen?: boolean;
  danger?: boolean;
}

/** A `className` replaces the default item styling, e.g. for colour swatches. */
export function ActionMenuItem({
  onSelect,
  transfersFocus = false,
  keepOpen = false,
  danger = false,
  className,
  children,
  ...buttonProps
}: ActionMenuItemProps): React.ReactElement {
  const state = useActionMenuState();
  return (
    <DropdownMenu.Item
      // Radix keeps the content mounted during the exit animation; without this a
      // second Enter or Space would run the action again.
      disabled={!state.open}
      asChild
      onPointerMove={(event) => {
        // Radix focuses the hovered item, which would pull focus out of a search field.
        if (document.activeElement instanceof HTMLInputElement) event.preventDefault();
      }}
      onSelect={(event) => {
        if (keepOpen) event.preventDefault();
        if (transfersFocus) state.focusTransfer.current = true;
        onSelect();
      }}
    >
      <button {...buttonProps} type="button" className={className ?? itemClass(danger)}>
        {children}
      </button>
    </DropdownMenu.Item>
  );
}

/** Text field inside a menu: typing must not reach the menu's typeahead, which steals focus. */
export function ActionMenuInput({
  onKeyDown,
  ...inputProps
}: React.ComponentProps<'input'>): React.ReactElement {
  return (
    <input
      {...inputProps}
      onKeyDown={(event) => {
        event.stopPropagation();
        onKeyDown?.(event);
        if (event.key !== 'ArrowDown' || event.defaultPrevented) return;
        event.preventDefault();
        event.currentTarget
          .closest('[role="menu"]')
          ?.querySelector<HTMLElement>('[role^="menuitem"]')
          ?.focus();
      }}
    />
  );
}

interface ActionMenuCheckboxItemProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children: React.ReactNode;
}

/** Toggle that leaves the menu open so several options can be changed in one visit. */
export function ActionMenuCheckboxItem({
  checked,
  onCheckedChange,
  children,
}: ActionMenuCheckboxItemProps): React.ReactElement {
  const state = useActionMenuState();
  return (
    <DropdownMenu.CheckboxItem
      disabled={!state.open}
      asChild
      checked={checked}
      onCheckedChange={onCheckedChange}
      onSelect={(event) => event.preventDefault()}
    >
      <button type="button" className={itemClass(false)}>
        {children}
      </button>
    </DropdownMenu.CheckboxItem>
  );
}

interface ActionMenuRadioGroupProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
}

export function ActionMenuRadioGroup({
  value,
  onValueChange,
  children,
}: ActionMenuRadioGroupProps): React.ReactElement {
  return (
    <DropdownMenu.RadioGroup value={value} onValueChange={onValueChange}>
      {children}
    </DropdownMenu.RadioGroup>
  );
}

/** Choice that leaves the menu open so the effect is visible straight away. */
export function ActionMenuRadioItem({
  value,
  children,
}: {
  value: string;
  children: React.ReactNode;
}): React.ReactElement {
  const state = useActionMenuState();
  return (
    <DropdownMenu.RadioItem
      disabled={!state.open}
      asChild
      value={value}
      onSelect={(event) => event.preventDefault()}
    >
      <button type="button" className={itemClass(false)}>
        {children}
      </button>
    </DropdownMenu.RadioItem>
  );
}

export function ActionMenuSub({ children }: { children: React.ReactNode }): React.ReactElement {
  return <DropdownMenu.Sub>{children}</DropdownMenu.Sub>;
}

interface ActionMenuSubTriggerProps extends Omit<React.ComponentPropsWithoutRef<'button'>, 'type'> {
  children: React.ReactNode;
}

export function ActionMenuSubTrigger({
  className,
  children,
  ...buttonProps
}: ActionMenuSubTriggerProps): React.ReactElement {
  const state = useActionMenuState();
  return (
    <DropdownMenu.SubTrigger disabled={!state.open} asChild>
      <button {...buttonProps} type="button" className={className ?? itemClass(false)}>
        {children}
      </button>
    </DropdownMenu.SubTrigger>
  );
}

const SUB_MENU_OFFSET = 6;

export function ActionMenuSubContent({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.SubContent
        data-menu-root
        className={className ? `menu menu--action ${className}` : 'menu menu--action'}
        aria-label={label}
        sideOffset={SUB_MENU_OFFSET}
        collisionPadding={VIEWPORT_MARGIN}
      >
        {children}
      </DropdownMenu.SubContent>
    </DropdownMenu.Portal>
  );
}
